import { useState } from "react";
import api, { API, fmtIDR, apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, XCircle, AlertTriangle, FileSpreadsheet } from "lucide-react";

export function ExcelImportModal({ open, onOpenChange, clientId, onImported }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const reset = () => setPreview(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "template_import_transaksi.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    finally { setLoading(false); e.target.value = ""; }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/import/commit", { client_id: clientId, rows: preview.rows });
      toast.success(`${data.inserted} transaksi diimport, ${data.skipped} dilewati`);
      onOpenChange(false); reset(); onImported && onImported();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> Import Massal via Excel</DialogTitle>
        </DialogHeader>

        {!preview && (
          <div className="py-6 space-y-4">
            <div className="flex items-center justify-between rounded-md border border-border p-4">
              <div>
                <p className="text-sm font-medium">1. Unduh template</p>
                <p className="text-xs text-muted-foreground">Isi sesuai kolom: tanggal, akun_bank, no_invoice, vendor, deskripsi, nominal, tipe, coa_code, project_code, tax_code</p>
              </div>
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template"><Download className="h-4 w-4 mr-2" />Template</Button>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border p-10 cursor-pointer hover:bg-secondary/50 transition-colors" data-testid="import-dropzone">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">{loading ? "Memproses..." : "2. Pilih file Excel / CSV"}</span>
              <span className="text-xs text-muted-foreground">Preview validasi ditampilkan sebelum commit</span>
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={onFile} data-testid="import-file" />
            </label>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Valid: <b className="num">{preview.valid}</b></span>
              <span className="flex items-center gap-1.5"><XCircle className="h-4 w-4 text-rose-600" /> Error: <b className="num">{preview.invalid}</b></span>
              <span className="text-muted-foreground">Total: {preview.total} baris</span>
            </div>
            <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead className="w-12">Baris</TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Nominal</TableHead>
                    <TableHead>Tipe</TableHead>
                    <TableHead>Status Validasi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row} className={r.valid ? "" : "bg-rose-50 dark:bg-rose-950/30"} data-testid={`import-row-${r.row}`}>
                      <TableCell className="num">{r.row}</TableCell>
                      <TableCell className="num text-xs">{fmtDate(r.data.date)}</TableCell>
                      <TableCell className="text-xs">{r.data.vendor}</TableCell>
                      <TableCell className="num text-right text-xs">{fmtIDR(r.data.amount)}</TableCell>
                      <TableCell className="text-xs uppercase">{r.data.flow}</TableCell>
                      <TableCell className="text-xs">
                        {r.errors.map((e, i) => (
                          <div key={i} className="flex items-center gap-1 text-rose-600"><XCircle className="h-3 w-3" />{e}</div>
                        ))}
                        {r.warnings.map((w, i) => (
                          <div key={i} className="flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />{w}</div>
                        ))}
                        {r.valid && r.warnings.length === 0 && <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />OK</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          {preview && <Button variant="outline" onClick={reset}>Pilih file lain</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Tutup</Button>
          {preview && (
            <Button onClick={commit} disabled={committing || preview.valid === 0} data-testid="import-commit-btn">
              {committing ? "Mengimport..." : `Import ${preview.valid} baris valid`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
